import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getCreatedActivities } from "@/services/api/activity";
import type { ActivityInfo } from "@/services/api/activity";
import ActivityFormWizard from "@/components/student/ActivityFormWizard";
import AdminEmptyState from "@/components/AdminEmptyState";
import AdminListSkeleton from "@/components/AdminListSkeleton";

const LOOKUP_PAGES = 5;

const StudentEditActivityPage: React.FC = () => {
  const { t } = useTranslation();
  const { activityId } = useParams<{ activityId: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const stateActivity = (location.state as { activity?: ActivityInfo } | null)?.activity;

  const [activity, setActivity] = useState<ActivityInfo | null>(stateActivity ?? null);
  const [loading, setLoading] = useState(!stateActivity);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!stateActivity) return;
    setActivity(stateActivity);
    setNotFound(false);
    setLoading(false);
  }, [stateActivity]);

  useEffect(() => {
    if (stateActivity || !activityId) return;
    let cancelled = false;
    const lookup = async () => {
      setLoading(true);
      for (let page = 1; page <= LOOKUP_PAGES; page += 1) {
        const resp = await getCreatedActivities(page);
        const result = resp?.result;
        if (!Array.isArray(result)) break;
        const match = result.find((item) => item.id === activityId);
        if (match) {
          if (cancelled) return;
          setActivity(match);
          setLoading(false);
          return;
        }
        if (result.length < 10) break;
      }
      if (cancelled) return;
      setLoading(false);
      setNotFound(true);
    };
    lookup();
    return () => {
      cancelled = true;
    };
  }, [stateActivity, activityId]);

  if (loading) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-6">
        <AdminListSkeleton count={3} />
      </div>
    );
  }

  if (!activity || notFound) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-10">
        <div className="rounded-2xl border border-gray-100 bg-white">
          <AdminEmptyState
            iconType="Ionicons"
            iconName="alert-circle-outline"
            title={t("Event not found")}
            message={t("This event is no longer available.")}
            actionLabel={t("Back to Home")}
            onAction={() => navigate("/student/home")}
          />
        </div>
      </div>
    );
  }

  return <ActivityFormWizard mode="edit" activity={activity} basePath="/student/activities" />;
};

export default StudentEditActivityPage;
